/**
 * LinkedCriteriaList Component
 *
 * Displays a list of criteria items linked to a playbook.
 * Each row shows:
 * - Criteria name
 * - Category badge (color swatch + name, or "No category" text)
 * - Archived indicator if criteria is archived
 * - Unlink button (hidden when onUnlink is undefined)
 *
 * Sorted: active items first, then alphabetically by name.
 *
 * Data is loaded once on mount and refreshed via cross-tab sync subscriptions
 * for playbook_criteria_items and market_condition_categories tables.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Pencil, Plus, X } from 'lucide-react';

import {
  Button,
  Card,
  CardBody,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@enfomentis/ui';
import { criteriaService } from '@/services/CriteriaService';
import { marketConditionCategoryService } from '@/services/MarketConditionCategoryService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';

export interface LinkedCriteriaListProps {
  /** Array of criteria IDs linked to the playbook */
  criteriaIds: string[];
  /** Handler for unlink action - hidden when undefined */
  onUnlink?: (id: string) => void;
  /** Handler for add existing action - opens picker modal */
  onAdd?: () => void;
  /** Handler for create new action - opens create modal */
  onCreateNew?: () => void;
  /** Handler for edit action - shows Edit button when defined */
  onEdit?: (criteria: PlaybookCriteriaItem) => void;
}

/** Sort by status (active first) then alphabetically by name (case-insensitive) */
const sortByStatusThenName = <T extends { status: string; name: string }>(
  items: T[]
): T[] => {
  return [...items].sort((a, b) => {
    // Active items first
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    // Then alphabetically by name (case-insensitive)
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
};

export const LinkedCriteriaList: React.FC<LinkedCriteriaListProps> = ({
  criteriaIds,
  onUnlink,
  onAdd,
  onCreateNew,
  onEdit,
}) => {
  // Local state for cached criteria and categories
  const [allCriteria, setAllCriteria] = useState<PlaybookCriteriaItem[]>([]);
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Shared popover state - single source so only one menu can be open
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  /**
   * Render the Add Criteria popover with "Create New..." and "Add Existing..." options.
   * Used in both empty state and list bottom to maintain consistency.
   */
  const renderAddPopover = (variant: 'flat' | 'light' = 'light') => {
    // Only show if at least one handler is defined
    if (!onAdd && !onCreateNew) return null;

    return (
      <Popover
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement="bottom"
      >
        <PopoverTrigger>
          <Button
            size="sm"
            variant={variant}
            color={variant === 'flat' ? 'primary' : 'default'}
            startContent={<Plus size={16} />}
            aria-label="Add Criteria"
          >
            Add Criteria
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-1">
          <div className="flex flex-col min-w-40">
            {onCreateNew && (
              <Button
                size="md"
                variant="light"
                className="justify-start"
                onPress={() => {
                  setIsPopoverOpen(false);
                  onCreateNew();
                }}
              >
                Create New...
              </Button>
            )}
            {onAdd && (
              <Button
                size="md"
                variant="light"
                className="justify-start"
                onPress={() => {
                  setIsPopoverOpen(false);
                  onAdd();
                }}
              >
                Add Existing...
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Load all criteria and categories data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [criteriaData, categoryData] = await Promise.all([
        criteriaService.getAll(),
        marketConditionCategoryService.getAll(),
      ]);
      setAllCriteria(criteriaData);
      setCategories(categoryData);
    } catch (error) {
      console.error('Failed to load criteria data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab sync for criteria changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('playbook_criteria_items', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to cross-tab sync for category changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('market_condition_categories', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Build category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, MarketConditionCategory>();
    categories.forEach((cat) => map.set(cat.id, cat));
    return map;
  }, [categories]);

  // Filter and sort linked criteria
  const linkedCriteria = useMemo(() => {
    const linked = allCriteria.filter((c) => criteriaIds.includes(c.id));
    return sortByStatusThenName(linked);
  }, [allCriteria, criteriaIds]);

  // Empty state
  if (!isLoading && linkedCriteria.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
        <div className="flex flex-col gap-1">
          <span className="text-text-primary text-sm font-medium">No criteria linked yet</span>
          <span className="text-text-secondary text-xs max-w-sm">
            Criteria are hard filters — every one must be met for the playbook to be valid.
            Add criteria to define what conditions must exist before you consider a trade.
          </span>
        </div>
        {renderAddPopover('flat')}
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-text-tertiary text-sm">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Subtitle */}
      <p
        className="text-xs text-text-secondary truncate"
        title="Required conditions that must ALL be met before trading"
      >
        Required conditions that must ALL be met before trading
      </p>

      {/* List items - scroll owned by parent PlaybookLinkageTabs CardBody */}
      <div className="flex flex-col space-y-2">
        {linkedCriteria.map((criteria) => {
          const category = criteria.marketConditionCategoryId
            ? categoryMap.get(criteria.marketConditionCategoryId)
            : undefined;
          const isArchived = criteria.status === 'archived';

          return (
            <Card
              key={criteria.id}
              radius="sm"
              shadow="none"
              className="border border-default-200"
            >
              <CardBody className="p-0">
                <div
                  className={`flex items-center gap-3 p-2 ${
                    isArchived ? 'opacity-60 bg-default-50' : ''
                  }`}
                >
                  {/* Name + Description + Archived Badge */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center min-w-0">
                      <span
                        className={`font-medium truncate text-sm ${
                          isArchived ? 'text-text-tertiary' : 'text-text-primary'
                        }`}
                      >
                        {criteria.name}
                      </span>
                      {isArchived && (
                        <Chip className="ml-2 shrink-0 text-xs text-text-tertiary bg-default-100">
                          Archived
                        </Chip>
                      )}
                    </div>
                    {criteria.description && (
                      <span className="text-sm text-text-secondary truncate block">
                        {criteria.description}
                      </span>
                    )}
                  </div>

                  {/* Category Badge */}
                  <div className="shrink-0">
                    {category ? (
                      <Chip
                        size="sm"
                        variant="flat"
                        className="text-xs"
                        style={{ backgroundColor: `${category.color}20`, color: category.color }}
                        startContent={
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                        }
                      >
                        {category.name}
                      </Chip>
                    ) : (
                      <span className="text-xs text-text-tertiary">No category</span>
                    )}
                  </div>

                  {/* Action cluster: Edit + Unlink */}
                  {(onEdit || onUnlink) && (
                    <div className="shrink-0 flex gap-1">
                      {onEdit && (
                        <Tooltip content="Edit Criteria">
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            aria-label={`Edit ${criteria.name}`}
                            onPress={() => onEdit(criteria)}
                          >
                            <Pencil size={16} />
                          </Button>
                        </Tooltip>
                      )}
                      {onUnlink && (
                        <Tooltip content="Unlink Criteria">
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            aria-label={`Unlink ${criteria.name}`}
                            onPress={() => onUnlink(criteria.id)}
                          >
                            <X size={16} />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>

      {/* Add button at bottom */}
      <div className="self-center">{renderAddPopover('light')}</div>
    </div>
  );
};
